import React, { useMemo, useState } from 'react';
import ResponsavelTransparencia from '../components/ResponsavelTransparencia';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  transparenciaService,
  type EmendaPublicaDTO,
  type TransparenciaFiltros,
} from '../services/transparenciaService';
import { emendaClassificationLabel, emendaNumeroExercicio } from '../utils/emendaFormatters';
import DashboardPagination from '../components/DashboardPagination';

type FontSize = 'normal' | 'grande' | 'maior';
type Contraste = 'normal' | 'alto';

const ANO_ATUAL = new Date().getFullYear();
const PRIMEIRO_EXERCICIO = 2026;
const ANOS = Array.from({ length: ANO_ATUAL - PRIMEIRO_EXERCICIO + 1 }, (_, index) => ANO_ATUAL - index);
const ESFERAS = ['Todos', 'Municipal', 'Estadual', 'Federal'];
const STATUS_OPTIONS = ['Todos', 'Recebido', 'Iniciado', 'Em execução', 'Concluído', 'Devolvido'];
const EXECUCAO_OPTIONS = [
  { value: 'Todos', label: 'Todas' },
  { value: 'EMPENHADO', label: 'Com empenho' },
  { value: 'SEM_EMPENHO', label: 'Sem empenho' },
  { value: 'PAGO', label: 'Com pagamento' },
  { value: 'SEM_PAGAMENTO', label: 'Sem pagamento' },
];
const POR_PAGINA_OPTIONS = [10, 20, 50, 100];

const readUrlState = (params: URLSearchParams) => {
  const exercicioParam = Number(params.get('exercicio'));
  const paginaParam = Number(params.get('pagina'));
  const porPaginaParam = Number(params.get('porPagina'));
  const esferaParam = params.get('esfera') || 'Todos';
  const tipoParam = params.get('tipo') || 'Todos';
  const statusParam = params.get('status') || 'Todos';
  const execucaoParam = params.get('execucao') || 'Todos';
  return {
    exercicio: Number.isInteger(exercicioParam) && exercicioParam > 0 ? exercicioParam : ANO_ATUAL,
    esfera: ESFERAS.includes(esferaParam) ? esferaParam : 'Todos',
    tipo: tipoParam,
    status: STATUS_OPTIONS.includes(statusParam) ? statusParam : 'Todos',
    execucao: EXECUCAO_OPTIONS.some(item => item.value === execucaoParam) ? execucaoParam : 'Todos',
    autor: params.get('autor') || '',
    objeto: params.get('objeto') || '',
    pagina: Number.isInteger(paginaParam) && paginaParam > 0 ? paginaParam : 1,
    porPagina: POR_PAGINA_OPTIONS.includes(porPaginaParam) ? porPaginaParam : 20,
  };
};

const formatCurrency = (value?: number): string => value == null
  ? 'Não informado'
  : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (value?: string): string => value
  ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
  : 'Não informado';

const formatDateTime = (value?: string): string => value
  ? new Date(value).toLocaleString('pt-BR')
  : 'Não informada';

const formatEnum = (value?: string): string => value
  ? ({ LIQUIDACAO: 'Liquidação', ANULACAO: 'Anulação' }[value] ?? value.replaceAll('_', ' ').toLocaleLowerCase('pt-BR').replace(/^./, char => char.toLocaleUpperCase('pt-BR')))
  : 'Não informado';

const numeroComAno = (numero?: string, ano?: number): string => numero
  ? `${numero}${ano ? `/${ano}` : ''}`
  : 'Não informado';

// Instrumento da parceria: o número do Termo de Fomento (vindo do Coplan) tem prioridade;
// sem ele, o número do convênio da emenda; sem nenhum, a modalidade do plano de trabalho.
const rotuloInstrumento = (emenda: EmendaPublicaDTO): string => {
  const termos = (emenda.contratos ?? []).filter(contrato => contrato.instrumento === 'TERMO_FOMENTO');
  if (termos.length > 0) return termos.map(termo => `Termo de Fomento ${numeroComAno(termo.numero, termo.ano)}`).join(', ');
  if (emenda.numeroConvenio) return numeroComAno(emenda.numeroConvenio, emenda.anoConvenio);
  return formatEnum(emenda.modalidadeInstrumento);
};

// Processo de compra: o do Termo de Fomento tem prioridade; sem ele, o do primeiro contrato que tiver.
const rotuloProcesso = (emenda: EmendaPublicaDTO): string => {
  const comProcesso = (emenda.contratos ?? []).filter(contrato => contrato.processoCompraNumero);
  const escolhido = comProcesso.find(contrato => contrato.instrumento === 'TERMO_FOMENTO') ?? comProcesso[0];
  return escolhido ? numeroComAno(escolhido.processoCompraNumero, escolhido.processoCompraAno) : 'Não informado';
};

const TransparenciaPublicaPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlState = readUrlState(searchParams);
  const [fontSize, setFontSize] = useState<FontSize>('normal');
  const [contraste, setContraste] = useState<Contraste>('normal');
  const [exercicio, setExercicio] = useState<number>(initialUrlState.exercicio);
  const [esfera, setEsfera] = useState(initialUrlState.esfera);
  const [tipo, setTipo] = useState(initialUrlState.tipo);
  const [statusSelecionado, setStatusSelecionado] = useState(initialUrlState.status);
  const [execucao, setExecucao] = useState(initialUrlState.execucao);
  const [autor, setAutor] = useState(initialUrlState.autor);
  const [objeto, setObjeto] = useState(initialUrlState.objeto);
  const [filtrosAtivos, setFiltrosAtivos] = useState<TransparenciaFiltros>({
    exercicio: initialUrlState.exercicio,
    esfera: initialUrlState.esfera === 'Todos' ? undefined : initialUrlState.esfera,
    tipo: initialUrlState.tipo === 'Todos' ? undefined : initialUrlState.tipo,
    status: initialUrlState.status === 'Todos' ? undefined : initialUrlState.status,
    execucao: initialUrlState.execucao === 'Todos' ? undefined : initialUrlState.execucao,
    autor: initialUrlState.autor || undefined,
    objeto: initialUrlState.objeto || undefined,
  });
  const [pagina, setPagina] = useState(initialUrlState.pagina);
  const [porPagina, setPorPagina] = useState(initialUrlState.porPagina);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [emendaAberta, setEmendaAberta] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transparencia-emendas', filtrosAtivos, pagina, porPagina],
    queryFn: () => transparenciaService.list(filtrosAtivos, pagina - 1, porPagina),
    staleTime: 5 * 60 * 1000,
  });
  const { data: tiposEmenda = [] } = useQuery({
    queryKey: ['transparencia-emendas-tipos'],
    queryFn: transparenciaService.tipos,
    staleTime: 30 * 60 * 1000,
  });
  const nomeTipo = (codigo?: string): string =>
    tiposEmenda.find(item => item.codigo === codigo)?.nome ?? emendaClassificationLabel(codigo);
  const { data: metadata } = useQuery({
    queryKey: ['transparencia-emendas-metadados'],
    queryFn: transparenciaService.metadata,
    staleTime: 5 * 60 * 1000,
  });

  const emendas = useMemo<EmendaPublicaDTO[]>(() => data?.items ?? [], [data]);

  const { data: totaisData } = useQuery({
    queryKey: ['transparencia-emendas-totais', filtrosAtivos],
    queryFn: () => transparenciaService.totais(filtrosAtivos),
    staleTime: 5 * 60 * 1000,
  });

  const totais = totaisData ?? { previsto: 0, empenhado: 0, pago: 0 };
  const totalItems = data?.totalItems ?? 0;

  const syncUrl = (filtros: TransparenciaFiltros, nextPage: number, nextPageSize: number) => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });
    if (nextPage > 1) params.set('pagina', String(nextPage));
    if (nextPageSize !== 20) params.set('porPagina', String(nextPageSize));
    setSearchParams(params, { replace: true });
  };

  const handleBuscar = () => {
    const novosFiltros: TransparenciaFiltros = {
      exercicio,
      esfera: esfera === 'Todos' ? undefined : esfera,
      tipo: tipo === 'Todos' ? undefined : tipo,
      status: statusSelecionado === 'Todos' ? undefined : statusSelecionado,
      execucao: execucao === 'Todos' ? undefined : execucao,
      autor: autor.trim() || undefined,
      objeto: objeto.trim() || undefined,
    };
    setPagina(1);
    setFiltrosAtivos(novosFiltros);
    syncUrl(novosFiltros, 1, porPagina);
  };

  const handleLimpar = () => {
    setPagina(1);
    setExercicio(ANO_ATUAL);
    setEsfera('Todos');
    setTipo('Todos');
    setStatusSelecionado('Todos');
    setExecucao('Todos');
    setAutor('');
    setObjeto('');
    const filtrosPadrao = { exercicio: ANO_ATUAL };
    setFiltrosAtivos(filtrosPadrao);
    syncUrl(filtrosPadrao, 1, porPagina);
  };

  const isAlto = contraste === 'alto';
  const pageClass = isAlto ? 'bg-black text-yellow-300' : 'bg-slate-50 text-slate-900';
  const cardClass = isAlto ? 'border border-yellow-600 bg-gray-950' : 'border border-slate-200 bg-white shadow-sm';
  const mutedClass = isAlto ? 'text-yellow-500' : 'text-slate-600';
  const inputClass = isAlto
    ? 'mt-1 w-full rounded-md border-yellow-600 bg-gray-900 text-yellow-200 focus:border-yellow-400 focus:ring-yellow-400'
    : 'mt-1 w-full rounded-md border-slate-300 bg-white text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500';
  const primaryClass = isAlto
    ? 'bg-yellow-400 text-black hover:bg-yellow-300'
    : 'bg-blue-700 text-white hover:bg-blue-800';
  const secondaryClass = isAlto
    ? 'border-yellow-600 text-yellow-300 hover:bg-gray-900'
    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50';
  const fontClass = fontSize === 'maior' ? 'text-xl' : fontSize === 'grande' ? 'text-lg' : 'text-base';

  return (
    <div className={`flex min-h-screen flex-col ${pageClass} ${fontClass}`}>
      <div className={isAlto ? 'border-b border-yellow-800 bg-gray-950' : 'border-b border-slate-200 bg-slate-100'}>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-1.5 sm:px-6 lg:px-8">
          <span className={`text-xs font-medium ${mutedClass}`}>Acessibilidade:</span>
          {(['normal', 'grande', 'maior'] as FontSize[]).map((size, index) => (
            <button key={size} type="button" onClick={() => setFontSize(size)}
              className={`rounded border px-2 py-0.5 text-xs ${fontSize === size ? primaryClass : secondaryClass}`}>
              {index === 0 ? 'A' : `A${'+'.repeat(index)}`}
            </button>
          ))}
          <button type="button" onClick={() => setContraste(value => value === 'normal' ? 'alto' : 'normal')}
            className={`ml-2 rounded border px-2 py-0.5 text-xs ${contraste === 'alto' ? primaryClass : secondaryClass}`}>
            {isAlto ? 'Contraste normal' : 'Alto contraste'}
          </button>
        </div>
      </div>

      <header className={isAlto ? 'border-b-2 border-yellow-400 bg-gray-950' : 'bg-blue-800 text-white'}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isAlto ? 'text-yellow-500' : 'text-blue-200'}`}>Portal da Transparência</p>
            <h1 className="mt-1 text-2xl font-bold">Emendas Parlamentares</h1>
            <p className={`mt-1 text-sm ${isAlto ? 'text-yellow-400' : 'text-blue-100'}`}>
              Identificação, repasses e execução orçamentária e financeira
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/transparencia/parlamentares" className={`rounded-md border px-4 py-2 text-sm font-semibold ${isAlto ? secondaryClass : 'border-white text-white hover:bg-blue-700'}`}>
              Consultar por parlamentar
            </Link>
            <Link to="/login" className={`rounded-md border px-4 py-2 text-sm font-semibold ${isAlto ? secondaryClass : 'border-white text-white hover:bg-blue-700'}`}>
              Acessar o sistema
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <section className={`flex flex-wrap items-center justify-between gap-3 rounded-xl px-5 py-3 text-sm ${cardClass}`} aria-label="Atualização dos dados">
          <p><span className="font-semibold">Período coberto:</span> {metadata?.primeiroExercicio ?? '—'} a {metadata?.ultimoExercicio ?? '—'}</p>
          <p><span className="font-semibold">Última atualização:</span> {formatDateTime(metadata?.ultimaAtualizacao)}</p>
          <p><span className="font-semibold">Registros publicados:</span> {(metadata?.totalRegistros ?? emendas.length).toLocaleString('pt-BR')}</p>
          {metadata?.responsavel && <ResponsavelTransparencia responsavel={metadata.responsavel} />}
        </section>

        <section className={`overflow-hidden rounded-xl ${cardClass}`} aria-label="Resumo das emendas">
          <div className="grid grid-cols-2 divide-x divide-y divide-slate-200 lg:grid-cols-4 lg:divide-y-0">
            {[
              ['Emendas', totalItems.toLocaleString('pt-BR')],
              ['Previsto', formatCurrency(totais.previsto)],
              ['Empenhado', formatCurrency(totais.empenhado)],
              ['Pago', formatCurrency(totais.pago)],
            ].map(([label, value]) => (
              <div key={label} className="px-4 py-3">
                <p className={`text-xs font-semibold uppercase tracking-wide ${mutedClass}`}>{label}</p>
                <p className="mt-1 text-base font-bold">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={`rounded-xl ${cardClass}`}>
          <button type="button" className="flex w-full items-center justify-between px-5 py-4 text-left" onClick={() => setFiltrosAbertos(value => !value)} aria-expanded={filtrosAbertos}>
            <span className="flex items-center gap-2 text-sm font-semibold"><FunnelIcon className="h-4 w-4" />Filtros de pesquisa</span>
            {filtrosAbertos ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          </button>
          {filtrosAbertos && (
            <div className={`border-t px-5 pb-5 pt-4 ${isAlto ? 'border-yellow-800' : 'border-slate-200'}`}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <label className="text-sm font-medium">Exercício<select value={exercicio} onChange={e => setExercicio(Number(e.target.value))} className={inputClass}>{ANOS.map(ano => <option key={ano}>{ano}</option>)}</select></label>
                <label className="text-sm font-medium">Origem<select value={esfera} onChange={e => setEsfera(e.target.value)} className={inputClass}>{ESFERAS.map(item => <option key={item}>{item}</option>)}</select></label>
                <label className="text-sm font-medium">Tipo de emenda<select value={tipo} onChange={e => setTipo(e.target.value)} className={inputClass}><option value="Todos">Todos</option>{tiposEmenda.map(item => <option key={item.codigo} value={item.codigo}>{item.nome}</option>)}</select></label>
                <label className="text-sm font-medium">Situação<select value={statusSelecionado} onChange={e => setStatusSelecionado(e.target.value)} className={inputClass}>{STATUS_OPTIONS.map(item => <option key={item}>{item}</option>)}</select></label>
                <label className="text-sm font-medium">Execução financeira<select value={execucao} onChange={e => setExecucao(e.target.value)} className={inputClass}>{EXECUCAO_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
                <label className="text-sm font-medium">Autor<input value={autor} onChange={e => setAutor(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleBuscar()} placeholder="Nome do parlamentar" className={inputClass} /></label>
                <label className="text-sm font-medium">Objeto<input value={objeto} onChange={e => setObjeto(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleBuscar()} placeholder="Descrição do objeto" className={inputClass} /></label>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={handleBuscar} className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${primaryClass}`}><MagnifyingGlassIcon className="h-4 w-4" />Buscar</button>
                <button type="button" onClick={handleLimpar} className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${secondaryClass}`}><XMarkIcon className="h-4 w-4" />Limpar</button>
                <a href={transparenciaService.exportUrl('csv', filtrosAtivos)} download className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${secondaryClass}`}><ArrowDownTrayIcon className="h-4 w-4" />Baixar dados em CSV</a>
                <a href={transparenciaService.exportUrl('json', filtrosAtivos)} download className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${secondaryClass}`}><ArrowDownTrayIcon className="h-4 w-4" />Baixar dados em JSON</a>
              </div>
            </div>
          )}
        </section>

        <section className={`overflow-hidden rounded-xl ${cardClass}`}>
          <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 ${isAlto ? 'border-yellow-800' : 'border-slate-200'}`}>
            <div>
              <h2 className="font-semibold">Resultado da pesquisa</h2>
              <p className={`mt-0.5 text-xs ${mutedClass}`}>Selecione uma emenda para consultar repasse, plano de trabalho e execução financeira.</p>
            </div>
          </div>

          {isLoading && <div className="py-16 text-center text-sm">Carregando dados...</div>}
          {isError && <div className="px-5 py-12 text-center"><p className="text-sm text-red-600">Não foi possível carregar os dados.</p><button type="button" onClick={() => refetch()} className={`mt-3 rounded-md border px-3 py-2 text-sm ${secondaryClass}`}>Tentar novamente</button></div>}
          {!isLoading && !isError && emendas.length === 0 && (
            <div className="px-5 py-14 text-center">
              <p className="font-semibold">Não houve emendas parlamentares no recorte consultado.</p>
              <p className={`mt-1 text-sm ${mutedClass}`}>Altere os filtros para consultar outra origem ou exercício.</p>
            </div>
          )}

          {!isLoading && !isError && emendas.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full divide-y divide-slate-200 text-sm">
                  <thead className={isAlto ? 'bg-gray-900 text-yellow-300' : 'bg-slate-50 text-slate-700'}>
                    <tr>{['Emenda', 'Origem / tipo', 'Autoria / beneficiário', 'Objeto', 'Processo', 'Valor previsto', 'Situação', 'Detalhar'].map(column => <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{column}</th>)}</tr>
                  </thead>
                  <tbody className={isAlto ? 'divide-y divide-gray-800' : 'divide-y divide-slate-100'}>
                    {emendas.map((emenda, index) => {
                      const key = emenda.id ?? `${emenda.numeroEmenda}-${index}`;
                      const aberta = emendaAberta === key;
                      const movimentos = emenda.execucaoFinanceira ?? [];
                      const identificacao = emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode);
                      return (
                        <React.Fragment key={key}>
                          <tr className={isAlto ? 'hover:bg-gray-900' : 'hover:bg-slate-50'}>
                            <td className="px-4 py-3"><p className="font-semibold">{identificacao.numero}/{identificacao.exercicio}</p><p className={`text-xs ${mutedClass}`}>{emenda.officialCode || 'Código oficial não informado'}</p></td>
                            <td className="px-4 py-3"><p>{emenda.esfera || 'Não informada'}</p><p className={`text-xs ${mutedClass}`}>{nomeTipo(emenda.classification)}</p></td>
                            <td className="px-4 py-3"><p className="font-medium">{emenda.councilorName || 'Autoria não informada'}</p><p className={`text-xs ${mutedClass}`}>{emenda.institutionName || 'Beneficiário não informado'}</p></td>
                            <td className="max-w-xs px-4 py-3"><p className="line-clamp-2">{emenda.objectDetail || 'Não informado'}</p><p className={`mt-1 text-xs ${mutedClass}`}>Função: {emenda.funcaoCodigo || 'não informada'}</p></td>
                            <td className="whitespace-nowrap px-4 py-3">{rotuloProcesso(emenda)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-medium">{formatCurrency(emenda.value)}</td>
                            <td className="px-4 py-3">{emenda.status || 'Não informada'}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col items-start gap-2">
                                <button type="button" onClick={() => setEmendaAberta(aberta ? null : key)} className={`inline-flex items-center gap-1 rounded-md border px-3 py-2 text-xs font-semibold ${secondaryClass}`} aria-expanded={aberta}>{aberta ? 'Fechar' : 'Consultar'}{aberta ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}</button>
                                {emenda.officialCode && (
                                  <Link to={`/transparencia/emendas/${encodeURIComponent(emenda.officialCode)}`} className={`text-xs font-semibold underline ${isAlto ? 'text-yellow-300 hover:text-yellow-100' : 'text-blue-700 hover:text-blue-900'}`}>
                                    Abrir página permanente
                                  </Link>
                                )}
                              </div>
                            </td>
                          </tr>
                          {aberta && (
                            <tr className={isAlto ? 'bg-gray-950' : 'bg-slate-50/70'}>
                              <td colSpan={8} className="px-5 py-5">
                                <div className="space-y-5">
                                  <section className={`rounded-lg border p-4 ${isAlto ? 'border-yellow-800' : 'border-slate-200 bg-white'}`}>
                                    <h3 className="text-sm font-bold">Identificação e repasse</h3>
                                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm lg:grid-cols-4">
                                      {[
                                        ['Forma de repasse', emenda.tipoTransferencia],
                                        ['Instrumento', rotuloInstrumento(emenda)],
                                        ['Início previsto', formatDate(emenda.periodoInicio)],
                                        ['Término previsto', formatDate(emenda.periodoFim)],
                                      ].map(([label, value]) => <div key={label}><dt className={`text-xs font-semibold uppercase ${mutedClass}`}>{label}</dt><dd className="mt-0.5 font-medium">{value || 'Não informado'}</dd></div>)}
                                    </dl>
                                    <div className={`mt-4 border-t pt-4 ${isAlto ? 'border-yellow-800' : 'border-slate-200'}`}><p className={`text-xs font-semibold uppercase ${mutedClass}`}>Objeto / finalidade</p><p className="mt-1 leading-6">{emenda.objectDetail || 'Não informado'}</p></div>
                                  </section>
                                  {(emenda.contratos?.length ?? 0) > 0 && (
                                    <section>
                                      <h3 className="text-sm font-bold">Contratos e Termo de Fomento</h3>
                                      <div className="mt-3 grid gap-3 lg:grid-cols-2">
                                        {emenda.contratos!.map((contrato, contratoIndex) => (
                                          <article key={`${contrato.numero}-${contrato.ano}-${contratoIndex}`} className={`rounded-lg border p-3 ${isAlto ? 'border-yellow-800' : 'border-slate-200 bg-white'}`}>
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                              <p className="font-semibold">{contrato.instrumento === 'TERMO_FOMENTO' ? 'Termo de Fomento' : 'Contrato'} {numeroComAno(contrato.numero, contrato.ano)}</p>
                                              <p className="whitespace-nowrap font-bold">{formatCurrency(contrato.valor)}</p>
                                            </div>
                                            <p className={`mt-1 text-xs ${mutedClass}`}>Vigência: {formatDate(contrato.vigenciaInicio)} a {formatDate(contrato.vigenciaFim)}</p>
                                            {contrato.processoCompraNumero && <p className={`mt-0.5 text-xs ${mutedClass}`}>Processo de compra: {numeroComAno(contrato.processoCompraNumero, contrato.processoCompraAno)}</p>}
                                            <p className="mt-2 break-words text-xs leading-5">{contrato.objeto || 'Objeto não informado'}</p>
                                          </article>
                                        ))}
                                      </div>
                                    </section>
                                  )}
                                  {(emenda.atosLegais?.length ?? 0) > 0 && (
                                    <section>
                                      <h3 className="text-sm font-bold">Atos legais da dotação</h3>
                                      <ul className="mt-3 space-y-2">
                                        {emenda.atosLegais!.map((ato, atoIndex) => (
                                          <li key={`${ato.numero}-${ato.ano}-${atoIndex}`} className={`rounded-lg border p-3 text-sm ${isAlto ? 'border-yellow-800' : 'border-slate-200 bg-white'}`}>
                                            <p className="font-semibold">Ato legal nº {numeroComAno(ato.numero, ato.ano)}{ato.dataRevogacao ? ` (revogado em ${formatDate(ato.dataRevogacao)})` : ''}</p>
                                            {ato.ementa && <p className="mt-1 text-xs leading-5">{ato.ementa}</p>}
                                            <p className={`mt-1 text-xs ${mutedClass}`}>Publicado em {formatDate(ato.dataPublicacao)}{ato.linkOficial && <> · <a href={ato.linkOficial} target="_blank" rel="noopener noreferrer" className="font-semibold underline">Texto oficial</a></>}</p>
                                          </li>
                                        ))}
                                      </ul>
                                    </section>
                                  )}
                                  <section>
                                    <h3 className="text-sm font-bold">Execução orçamentária e financeira</h3>
                                    <div className="mt-3 grid grid-cols-3 gap-2">
                                      {[['Empenhado', emenda.valorEmpenhado], ['Liquidado', emenda.valorLiquidado], ['Pago', emenda.valorPago]].map(([label, value]) => <div key={String(label)} className={`rounded-lg border p-3 ${isAlto ? 'border-yellow-800' : 'border-slate-200 bg-white'}`}><p className={`text-xs ${mutedClass}`}>{label}</p><p className="mt-1 font-bold">{formatCurrency(value as number | undefined)}</p></div>)}
                                    </div>
                                    {movimentos.length === 0 ? <p className={`mt-4 rounded-lg border border-dashed p-4 text-sm ${mutedClass}`}>Não houve execução orçamentária e financeira registrada para esta emenda no exercício consultado.</p> : (
                                      <div className="mt-4 grid gap-3 lg:grid-cols-3">
                                        {movimentos.map((movimento, movimentoIndex) => (
                                          <article key={`${movimento.numeroDocumento}-${movimentoIndex}`} className={`rounded-lg border p-3 ${isAlto ? 'border-yellow-800' : 'border-slate-200 bg-white'}`}>
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                              <div>
                                                <p className="font-semibold">{formatEnum(movimento.tipo)}{movimento.natureza === 'ANULACAO' ? ' (anulação)' : ''}</p>
                                                <p className={`mt-0.5 text-xs ${mutedClass}`}>{movimento.numeroDocumento || 'Documento não informado'} · {formatDate(movimento.dataMovimento)}</p>
                                              </div>
                                              <p className="whitespace-nowrap font-bold">{formatCurrency(movimento.valor)}</p>
                                            </div>
                                            <div className="mt-3 grid gap-3 text-xs">
                                              <div>
                                                <p className={`font-semibold uppercase ${mutedClass}`}>Credor</p>
                                                <p className="mt-1 break-words">{movimento.credor || emenda.institutionName || 'Não informado'}</p>
                                              </div>
                                              <div>
                                                <p className={`font-semibold uppercase ${mutedClass}`}>Descrição da despesa</p>
                                                <p className="mt-1 break-words leading-5">{movimento.descricaoDespesa || emenda.objectDetail || 'Não informado'}</p>
                                              </div>
                                            </div>
                                          </article>
                                        ))}
                                      </div>
                                    )}
                                  </section>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <DashboardPagination
                page={pagina}
                pageSize={porPagina}
                totalItems={totalItems}
                onPageChange={value => { setPagina(value); syncUrl(filtrosAtivos, value, porPagina); }}
                onPageSizeChange={value => { setPagina(1); setPorPagina(value); syncUrl(filtrosAtivos, 1, value); }}
                pageSizeOptions={POR_PAGINA_OPTIONS}
                itemLabel="emendas"
              />
            </>
          )}
        </section>
      </main>

      <footer className={`border-t ${isAlto ? 'border-yellow-800 bg-gray-950' : 'border-slate-200 bg-white'}`}>
        <div className={`mx-auto max-w-7xl px-4 py-4 text-center text-xs ${mutedClass}`}>Portal da Transparência Municipal - Emendas Parlamentares</div>
      </footer>
    </div>
  );
};

export default TransparenciaPublicaPage;
